// src/pages/doctor/DoctorDashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchTodaySummary,
  type TodaySummary,
  fetchNextReservationMine,
  type NextReservation,
} from '../../api/doctorDashboard';
import api from '../../api/axios';
import PendingTasksPanel from '../../components/PendingTasksPanel';

const DoctorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [doctorName, setDoctorName] = useState<string>('医師');

  // サマリー
  const [summary, setSummary] = useState<TodaySummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // 次の予約（自分）
  const [nextRes, setNextRes] = useState<NextReservation | null>(null);
  const [loadingNext, setLoadingNext] = useState(false);

  // 医師名取得
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/doctor/me');
        const name = res.data?.profile?.full_name || res.data?.name || res.data?.email || '医師';
        setDoctorName(name);
      } catch (err) {
        console.error('Failed to fetch doctor info', err);
      }
    })();
  }, []);

  // サマリー + 次の予約取得
  useEffect(() => {
    (async () => {
      try {
        setLoadingSummary(true);
        setLoadingNext(true);
        const [summaryData, nextData] = await Promise.all([
          fetchTodaySummary(),
          fetchNextReservationMine(),
        ]);
        setSummary(summaryData || null);
        setNextRes(nextData ?? null);
      } catch {
        setSummary(null);
        setNextRes(null);
      } finally {
        setLoadingSummary(false);
        setLoadingNext(false);
      }
    })();
  }, []);

  // サマリー互換処理
  const totalToday = (summary as any)?.total_today_all ?? summary?.total ?? 0;
  const completed = summary?.completed ?? 0;
  const canceled = (summary as any)?.doctor_cancelled != null
    ? (summary as any).canceled + (summary as any).doctor_cancelled
    : summary?.canceled ?? 0;
  const expired = summary?.expired ?? 0;

  // 新フィールド（存在しない場合は非表示）
  const poolFuture = (summary as any)?.pool_future ?? null;
  const assignedToday = (summary as any)?.assigned_today ?? null;
  // const reservedLegacyToday = (summary as any)?.reserved_today ?? summary?.reserved ?? null;

  // 言語判定（必要なら文案切り替え）
  const lang = i18n?.language?.toLowerCase?.() || (typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'ja');
  const isJa = lang.startsWith('ja');
  const isZh = lang.startsWith('zh');

  const browserHint = t('doctor.browser_join_hint_short') ||
    (isJa
      ? '※ 開始時にアプリを促されたら「キャンセル」→ ページの「ブラウザから参加」をクリックしてください。'
      : '※ 弹窗提示应用时，请先点“取消”，再点击页面中的“从浏览器加入”。');

  const nextActionsNote = t('doctor.next_actions_hint_no_link') ||
    (isJa
      ? 'より詳しい操作（完了／キャンセル／プールに戻す）は「予約一覧」で行えます。'
      : '更详细的操作（完成/取消/退回到池）请前往「预约列表」。');

  const cards = [
    {
      icon: '📋',
      title: t('doctor.tab_reservations'),
      description: t('doctor.menu_reservations_desc'),
      path: '/doctor/reservations',
    },
    {
      icon: '👥',
      title: t('doctor.tab_users'),
      description: t('doctor.menu_users_desc'),
      path: '/doctor/users',
    },
    {
      icon: '🗓️',
      title: t('doctor.tab_schedule'),
      description: t('doctor.menu_schedule_desc'),
      path: '/doctor/schedule',
    },
  ];

  return (
    <div className="w-full min-h-screen bg-transparent py-12 px-4 sm:px-8 font-sans">
      <div className="max-w-5xl mx-auto">
        {/* ウェルカム */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('doctor.title')}</h1>
          <p className="text-gray-500 text-sm">{t('doctor.welcome', { name: doctorName })}</p>
        </div>

        {/* 今日のサマリー + 次の予約 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* サマリーカード */}
          <div className="bg-white rounded-2xl shadow-soft p-6 lg:col-span-2 border border-border/50">
            <h3 className="text-lg font-semibold mb-3">📅 {t('admin.today_summary')}</h3>
            {loadingSummary ? (
              <div className="text-sm text-gray-500">{t('loading') || '読み込み中…'}</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-gray-700">
                  <div className="p-3 rounded bg-gray-50">
                    <div className="text-xs text-gray-500">{t('doctor.total_today') || '本日の合計'}</div>
                    <div className="text-xl font-bold">{totalToday}</div>
                  </div>

                  {assignedToday != null && (
                    <div className="p-3 rounded bg-gray-50">
                      <div className="text-xs text-gray-500">{t('doctor.assigned_today') || '今日・割当済み'}</div>
                      <div className="text-xl font-bold">{assignedToday}</div>
                    </div>
                  )}
                  {poolFuture != null && (
                    <div className="p-3 rounded bg-gray-50">
                      <div className="text-xs text-gray-500">{t('doctor.pool_future') || '予約プール（未来）'}</div>
                      <div className="text-xl font-bold">{poolFuture}</div>
                    </div>
                  )}

                  <div className="p-3 rounded bg-gray-50">
                    <div className="text-xs text-gray-500">{t('doctor.completed')}</div>
                    <div className="text-xl font-bold">{completed}</div>
                  </div>
                  <div className="p-3 rounded bg-gray-50">
                    <div className="text-xs text-gray-500">{t('doctor.cancelled')}</div>
                    <div className="text-xl font-bold">{canceled}</div>
                  </div>
                  <div className="p-3 rounded bg-gray-50">
                    <div className="text-xs text-gray-500">{t('mypage.expired')}</div>
                    <div className="text-xl font-bold">{expired}</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 次の予約カード */}
          <div className="bg-white rounded-2xl shadow-soft p-6 border border-border/50">
            <h3 className="text-lg font-semibold mb-3">⏭️ {t('doctor.next_today') || '今日の次の予約'}</h3>
            {loadingNext ? (
              <div className="text-sm text-gray-500">{t('searching') || '検索中…'}</div>
            ) : nextRes ? (
              <div className="text-sm text-gray-800 space-y-2">
                <div>
                  <span className="font-medium">{nextRes.date}</span> {nextRes.time}
                </div>
                <div>{t('doctor.patient')}: {nextRes.user_name ?? '-'}</div>
                <div>{t('doctor.service')}: {nextRes?.service ? t(`service.${nextRes.service}`, nextRes.service) : '-'}</div>
                {nextRes.zoom_meeting_id && (
                  <div className="text-[10px] text-gray-500 font-mono mt-1">ID: {nextRes.zoom_meeting_id}</div>
                )}

                {/* 入室ボタン：診療室（三栏レイアウト）へ内部遷移 */}
                <div className="flex flex-col items-start gap-2 pt-1">
                  <button
                    className="inline-block px-6 py-2 text-xs rounded-full bg-coral text-white font-bold shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all"
                    onClick={() => navigate(`/doctor/consultation/${nextRes.id}`)}
                  >
                    {t('doctor.enter_consultation') || '入室する'}
                  </button>
                </div>

                {/* 「予約一覧へ」のリンクは削除。説明だけ残す */}
                <div className="text-[11px] text-gray-500 leading-tight pt-1">
                  {nextActionsNote}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">{t('doctor.no_next_today') || '本日これからの予約はありません'}</div>
            )}
          </div>
        </div>

        {/* 未処理タスクパネル */}
        <div className="bg-white rounded-2xl shadow-soft p-6 mb-10 border border-border/50">
          <PendingTasksPanel role="doctor" />
        </div>

        {/* モジュールカード */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, index) => (
            <div
              key={index}
              onClick={() => navigate(card.path)}
              className="bg-white rounded-2xl shadow-soft p-8 cursor-pointer hover:bg-coral/5 hover:shadow-card hover:-translate-y-1 transition-all border border-border/50"
            >
              <h3 className="text-lg font-semibold mb-2">{card.icon} {card.title}</h3>
              <p className="text-sm text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default DoctorDashboard;
